import React, {ReactElement} from 'react';
import moment from 'moment';
import {Typography} from '@customink/pigment-react';
import Box from '@mui/material/Box';
import Skeleton from '@mui/material/Skeleton';
import {useGetStoreProductQuery} from '../../api/storesApi';
import {useGroupOrderTimeline, useDomPurify} from '../../hooks';
import {useStoreType} from '../../hooks/useStoreType';
import ProductGrid from '../ProductGrid';
import './index.scss';

type Props = {
  storeSlug: string;
  productId: number;
};
const NUMOFLIST = 4;

const PlaceHolder = () => {
  const content = Array.from(Array(NUMOFLIST).keys()).map(item => {
    return <Skeleton className="content" key={item} />;
  });

  return (
    <Box className="placeHolder">
      <div className="placeHolder-feature">
        <Skeleton className="title" />
        {content}
      </div>
      <div className="placeHolder-delivery">
        <Skeleton className="title" />
        {content}
      </div>
    </Box>
  );
};

const FeatureAndDelivery: React.FC<Props> = ({
  storeSlug,
  productId
}: Props): ReactElement => {
  const {sanitizer} = useDomPurify();

  const {data: product} = useGetStoreProductQuery({
    urlSlug: storeSlug,
    productId
  });

  const {isStoreTypeGroupOrder} = useStoreType();
  const {closeDateFormatted} = useGroupOrderTimeline();

  const closeDate = isStoreTypeGroupOrder
    ? closeDateFormatted
    : moment(product?.fulfillmentDate).format('dddd, MMMM D');

  const Feature = () => {
    return (
      <div className="feature">
        <Typography variant="bodyShort1" className="subTitle">
          Features
        </Typography>
        <div
          className="content"
          dangerouslySetInnerHTML={{__html: sanitizer(product?.features)}}
        />
      </div>
    );
  };

  const Delivery = () => {
    return (
      <div className="delivery">
        <Typography variant="bodyShort1" className="subTitle">
          Production & Delivery
        </Typography>
        <div className="content">
          <ul>
            <li>This group order closes on {closeDate}.</li>
            <li>
              Your organizer has 2 weeks from the close date to complete the
              order. Your order will ship approximately 2 weeks after the order
              is complete.
            </li>
            <li>
              If the order does not meet the goal, the organizer may choose to
              cancel it. If that happens, you&apos;ll be refunded automatically.
            </li>
          </ul>
        </div>
      </div>
    );
  };

  return (
    <div className="storeProductFeatureAndDelivery">
      {product ? (
        <ProductGrid.Container justifyContent="space-evenly">
          <ProductGrid.Item
            numColLg={2.1}
            numColMd={1}
            gridKey="feature"
            numColSm={1}>
            <Feature />
          </ProductGrid.Item>
          <ProductGrid.Item
            numColLg={1.9}
            numColMd={1}
            gridKey="delivery"
            numColSm={1}>
            <Delivery />
          </ProductGrid.Item>
        </ProductGrid.Container>
      ) : (
        <PlaceHolder />
      )}
    </div>
  );
};

export default FeatureAndDelivery;
